import { useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { profile as profileApi } from '@discovr/api';
import { useProfile } from '@discovr/common';
import { OUTREACH_CURRENCIES } from '@discovr/types';

/** Settings row for the per-user display currency. Saves on change; "Automatic" clears the choice. */
export function DisplayCurrencyField() {
  const id = useId();
  const queryClient = useQueryClient();
  const { data: me } = useProfile();
  const saved = (me as { user?: { display_currency?: string } } | undefined)?.user?.display_currency ?? me?.display_currency ?? '';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (next: string) => {
    setSaving(true);
    setError(null);
    try {
      await profileApi.setDisplayCurrency(next);
      // Every money response is rendered in the display currency, so refetch them all.
      await queryClient.invalidateQueries();
    } catch (e: unknown) {
      setError((e as { message?: string }).message ?? 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <label htmlFor={id} className="m-0 text-body-sm-medium text-foreground">Display currency</label>
        <p className="m-0 text-caption-regular text-muted-foreground">
          Converted figures are shown in this currency. Nothing stored or booked changes.
        </p>
        {error && <p role="alert" className="m-0 mt-1 text-caption-regular text-destructive">{error}</p>}
      </div>
      <select
        id={id}
        value={saved}
        onChange={(e) => handleChange(e.target.value)}
        disabled={saving}
        className="h-9 shrink-0 rounded-md border border-border bg-background px-2 text-body-sm-regular text-foreground"
      >
        <option value="">Automatic</option>
        {OUTREACH_CURRENCIES.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </div>
  );
}
